export type SelectionAnswer = {
  question_id: number;
  option_id: string;
};

export type SelectionQuestion = {
  id: number;
  name: string;
  options: [];
};

// Mirrors Drupal\ws_selection\Model\SelectionQuestion::CATEGORY_QUESTION_ID.
export const CATEGORY_QUESTION_ID = -101;

// Mirrors Drupal\ws_selection\Model\SelectionAnswer::RANGE_SEPARATOR.
export const RANGE_SEPARATOR = `~`;

// Mirrors Drupal\ws_selection\Model\SelectionOption::NO_PREFERENCE.
export const NO_PREFERENCE = `_no preference_`;

export type SelectionState = {
  active: boolean;
  categoryId: number | null;
  dialogTitle: string | null;
  currentQuestionIndex: number;
  postedQuestionIndexes: number[];
  sessionId: string | null;
  sessionExpiresAt: number | null;
  answers: SelectionAnswer[];
  generation: number;
};

type Listener = (state: SelectionState) => void;

class SelectionStore {
  private state: SelectionState = {
    active: false,
    categoryId: null,
    dialogTitle: null,
    currentQuestionIndex: 0,
    postedQuestionIndexes: [],
    sessionId: null,
    sessionExpiresAt: null,
    answers: [],
    generation: 0,
  };
  private listeners: Listener[] = [];
  private startCategoryId: number | null = null;

  subscribe(listener: Listener) {
    this.listeners.push(listener);
    listener(this.state);

    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  start(categoryId: number | null = null, dialogTitle: string | null) {
    this.startCategoryId = categoryId;
    this.state = {
      active: true,
      categoryId,
      dialogTitle: dialogTitle,
      currentQuestionIndex: 0,
      postedQuestionIndexes: [],
      sessionId: null,
      sessionExpiresAt: null,
      answers: [],
      generation: this.state.generation + 1,
    };
    this.notify();
  }

  restart() {
    this.start(this.startCategoryId, this.state.dialogTitle);
  }

  setSessionExpiresIn(seconds: number) {
    this.state = {
      ...this.state,
      sessionExpiresAt: Date.now() + seconds * 1000,
    };
    this.notify();
  }

  isSessionExpired() {
    return this.state.sessionExpiresAt !== null && Date.now() >= this.state.sessionExpiresAt;
  }

  nextQuestion() {
    this.state = {
      ...this.state,
      currentQuestionIndex: this.state.currentQuestionIndex + 1,
    };
    this.notify();
  }

  previousQuestion() {
    this.state = {
      ...this.state,
      currentQuestionIndex: Math.max(0, this.state.currentQuestionIndex - 1),
    };
    this.notify();
  }

  addAnswer(question_id: number, option_id: string) {
    const answers = this.state.answers.filter(answer => answer.question_id !== question_id);
    answers.push({ question_id, option_id });

    this.state = {
      ...this.state,
      answers,
    };
    this.notify();
  }

  setSessionId(session_id: string) {
    this.state = {
      ...this.state,
      sessionId: session_id,
    };
    this.notify();
  }

  setCategoryId(category_id: number) {
    if (this.state.categoryId !== null && category_id !== this.state.categoryId) {
      this.state = {
        ...this.state,
        answers: this.state.answers.filter(answer => answer.question_id === CATEGORY_QUESTION_ID),
      };
    }

    this.state = {
      ...this.state,
      categoryId: category_id,
    };
    this.notify();
  }

  getCategoryId() {
    return this.state.categoryId;
  }

  setCurrentQuestionIndex(value: number) {
    this.state.currentQuestionIndex = value;
    this.notify();
  }

  getAnswers() {
    return this.state.answers;
  }

  getAnswer(question: { id: number; options: SelectionOption[] }) {
    const answer = this.state.answers.find(answer => answer.question_id === question.id);

    if (answer?.option_id === NO_PREFERENCE) {
      return question.options.find(option => option.id === NO_PREFERENCE) ?? {
        id: NO_PREFERENCE,
        label: ``,
        description: ``,
      };
    }

    if (answer) {
      const [min, max] = answer.option_id.split(RANGE_SEPARATOR).map(Number);
      if (!isNaN(min) && !isNaN(max)) {
        return { min, max };
      }
    }

    return {
      min: Number(question.options[0]?.id),
      max: Number(question.options[1]?.id),
    };
  }

  /**
   * Answers for questions beyond the current one belong to an abandoned
   * path (e.g. after going back and answering differently) and shouldn't
   * be sent to the backend.
   */
  getAnswersUpToCurrentQuestion(questions: { id: number }[]): SelectionAnswer[] {
    const knownQuestionIds = new Set(
      questions.slice(0, this.state.currentQuestionIndex + 1).map(question => question.id)
    );

    return this.state.answers.filter(answer => knownQuestionIds.has(answer.question_id));
  }

  addPostedQuestionIndex(value: number) {
    if (this.state.postedQuestionIndexes.includes(value)) {
      return;
    }

    this.state = {
      ...this.state,
      postedQuestionIndexes: [...this.state.postedQuestionIndexes, value],
    };
    this.notify();
  }

  getPostedQuestionIndexes() {
    return this.state.postedQuestionIndexes;
  }

  wasPosted(value: number) {
    return this.state.postedQuestionIndexes.includes(value);
  }

  getSessionId() {
    return this.state.sessionId;
  }

  getDialogTitle() {
    return this.state.dialogTitle;
  }

  private notify() {
    this.listeners.forEach(listener => listener(this.state));
  }

}

export const selectionStore = new SelectionStore();
